import { BasicEntity } from '@wizeoo/akm-message-lib';

/**
 * Interface pour les classes d'encapsulation des VOs
 * (compositions des objets métiers)
 */
export interface IVo {
  id: number; // clé technique
  uid: string; // UID  DE LA SOURCE pour synchro
  domain: string; // le domaine
  businessUnitCode: string; // la BU
  source: BasicEntity; // L'objet métier véritable
  updateTime?: number; // Dernière date d'update du VO, utilisé dans le gestion des versions
  refCode?: string; // refcode pour les EMs
}

/**
 * décorateur des VO pour enregistrement automatique des classes "métier"
 * enregistrement de la PORTEE des EMs associés
 * @param fqn la classe java cible, fullQualified, alimentée auto par l'annotation @entity
 * @param range : la portée des EMs sur cette classe: BU, User, Vision...
 */
export function entity(fqn: string, range: string): any {
  return (target) => {
    EMMaps.setRange(target, fqn, range);
    // console.log('entity', target.name, fqn, range);
    return target;
  };
}

/**
 * décorateur des VO pour enregistrement automatique des champs "métier"
 * @param kind la classe du champ alimentée auto par l'annotation @field(1...)
 * @param collection le champ est une collection, alimenté auto par l'annotation @field(...2)
 * @param keyPairs le champ est un objet de type keyPair
 */
export function field(kind: string, collection: boolean = false, keyPairs: boolean = false): any {
  return (target, key) => {
    EMMaps.addField(target, key, kind, collection, keyPairs);
    return target;
  };
}

/**
 * les maps des propriétés d'objets à tracer (EMs sur objets complexes)
 */
export class EMMaps {
  /**
   * la map de portée des EMs (BU / Vision...)
   * sous la forme [class] => [fqn: string, range: string]
   */
  private static _rangeMap: Map<string, [fqn: string, range: string]> = null;

  /**
   * la map globale des champs par classe
   * sous la forme [class] => [fieldName, [classe du champ, collection(boolean), keyPairs(boolean)]]
   */
  private static _map: Map<any, Map<string, [string, boolean, boolean]>> = null;

  /**
   * private. Appelée par le décorateur @Entity
   * @param className la classe simple côté front (machinVO)
   * @param fqn le nom complet de l'objet métier côté back
   * @param range la portée (impact) de la classe
   */
  public static setRange(className: any, fqn: string, range: string): void {
    if (!this._rangeMap) {
      this._rangeMap = new Map<any, [fqn: string, range: string]>();
    }
    this._rangeMap.set(className, [fqn, range]);
  }
  /**
   * @param className Pour une classe donnée (machinVO), retourne la portée de modifs des EMs
   */
  public static getRange(className: any): [fqn: string, range: string] {
    return this._rangeMap.get(className);
  }

  /**
   * Auto ajout d'une classe par le décorateur @entity
   * si ELLE n'EXISTE PAS
   * @param className la classe à ajouter
   * @return la map correspondante
   */
  private static addClass(className: string): Map<string, [string, boolean, boolean]> {
    if (!this._map) {
      this._map = new Map<string, Map<string, [string, boolean, boolean]>>();
    }
    if (!this._map.has(className)) {
      // si n'existe pas
      this._map.set(className, new Map<string, [string, boolean, boolean]>());
    }
    return this._map.get(className);
  }

  /**
   * Auto ajout d'un champ par le décorateur @field
   * @param target la méthode (constructeur de classe)
   * @param key : le nom du champ
   * @param kind: la classe du champ (string)
   * @param collection : le champ est une collection
   * @param keyPairs le champ est un objet de type keyPair
   */
  public static addField(target: any, key: string, kind: string, collection: boolean, keyPairs: boolean): void {
    // déléguation de l'ajout de classe à la méthode dédiée
    this.addClass(target.constructor).set(key, [kind, collection, keyPairs]);
  }

  /**
   * Pour une classe donnée, retourne la map des champs à comparer et leur type
   * @param objectClass la classe cible
   */
  public static getFieldsMap(objectClass: any): Map<string, [string, boolean, boolean]> {
    return this._map.get(objectClass);
  }
}
